import { useApolloClient } from "@apollo/client";
import { Text, Avatar } from "@mantine/core";
import {
  GetOrganizationServicePageDocument,
  GetOrganizationServicePageQuery,
  GetOrganizationServicePageQueryVariables,
} from "src/generated/graphql";
import { FetchParams, QueryResponse } from "@kapex/tables";
import { ArrayElement } from "src/utils/helperTypes";
import { useCallback, useContext, useEffect } from "react";
import { AppContext, useAppContext } from "src/api/AppContext";
import { useNavigate } from "react-router-dom";
import {
  DEFAULT_ORDER,
  EMPLOYEE_ROLE_OBJ,
  ROUTES,
  ROUTE_NAMES,
} from "src/utils/constants";
import { Button } from "@mantine/core";
import { useModals } from "@mantine/modals";
import ServiceDetail from "../ServiceDetail";
import { BiUserCircle } from "react-icons/bi";
import ScrollableDataGrid from "src/components/ScrollableDataGrid";
import useWritePermission from "src/hooks/usePermission";
import LocationServiceDetail from "src/modules/LocationService/LocationServiceDetail";

export type OrganizationServiceData = ArrayElement<
  GetOrganizationServicePageQuery["organizationServicePage"]["items"]
>;

const ServiceList = () => {
  const modals = useModals();
  const navigate = useNavigate();
  const { setCurrentPageTitle } = useContext(AppContext);
  const { query } = useApolloClient();
  const { currentLocation } = useAppContext();
  const isLocationService =
    window.location.pathname === ROUTES.LOCATION_SERVICE;
  const isAllowWrite = useWritePermission(
    isLocationService
      ? EMPLOYEE_ROLE_OBJ.locServices
      : EMPLOYEE_ROLE_OBJ.orgService
  );

  const getOrganizationServices = async (
    fetchParams: FetchParams
  ): Promise<QueryResponse<OrganizationServiceData>> => {
    let { data } = await query<
      GetOrganizationServicePageQuery,
      GetOrganizationServicePageQueryVariables
    >({
      query: GetOrganizationServicePageDocument,
      variables: {
        pagination: {
          count: fetchParams.count,
          page: fetchParams.page,
          orderBy:
            fetchParams.orderBy || DEFAULT_ORDER.organizationservice_name,
          filters: isLocationService
            ? [
                ...(fetchParams.filters || []),
                {
                  field: "locationId",
                  type: "text",
                  value: currentLocation!.id,
                  parentRelationField: "locationServices",
                },
              ]
            : fetchParams.filters,
        },
      },
    });

    if (data.organizationServicePage) {
      return {
        rowCount: data.organizationServicePage.totalRows,
        rowData: data.organizationServicePage
          .items as OrganizationServiceData[],
      };
    }
    return {
      rowData: [],
      rowCount: 0,
    };
  };

  useEffect(() => {
    setCurrentPageTitle(ROUTE_NAMES.SERVICE);

    return () => {
      setCurrentPageTitle(undefined);
    };
  }, []);

  const handleNavigation = (detail: OrganizationServiceData): void => {
    let id = detail.id;
    if (isLocationService) {
      id =
        detail.locationServices.find(
          (ls) => ls.location.id === currentLocation?.id!
        )?.id || detail.id;
    }
    navigate(
      `${isLocationService ? ROUTES.LOCATION_SERVICE : ROUTES.SERVICE}/${id}`
    );
  };

  const handleModal = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Create Service",
      children: <ServiceDetail isEdit={false} />,
    });
  }, []);

  const handleModalLocService = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Create Location Service",
      children: <LocationServiceDetail isEdit={false} />,
    });
  }, []);

  const ShowTable = useCallback(() => {
    return (
      <ScrollableDataGrid<OrganizationServiceData>
        topMargin={56}
        columnConfigs={[
          {
            field: "logoUrl",
            name: "",
            sortable: false,
            cellRenderer: (rowItem) => {
              return rowItem.imgUrl ? (
                <Avatar
                  sx={{
                    ":root": {
                      justifyContent: "center",
                      alignItems: "center",
                      display: "flex",
                    },
                  }}
                  size="sm"
                  src={rowItem.imgUrl}
                />
              ) : (
                <div style={{ padding: 0, borderRadius: 18 }}>
                  <BiUserCircle size={26} />
                </div>
              );
            },
          },
          {
            field: "organizationservice_name",
            name: "Name",
            filter: {
              field: "name",
              type: "text",
            },
            sortable: true,
            cellRenderer: (rowItem) => (
              <Text
                weight={500}
                onClick={() => handleNavigation(rowItem)}
                style={{ cursor: "pointer" }}
              >
                {rowItem.name}
              </Text>
            ),
          },
          {
            field: "duration",
            name: "Duration",
            filter: {
              field: "duration",
              type: "text",
            },
            sortable: true,
            cellRenderer: (rowItem) => (
              <Text
                weight={500}
                onClick={() => handleNavigation(rowItem)}
                style={{ cursor: "pointer" }}
              >
                {rowItem.duration}
              </Text>
            ),
          },
          {
            field: "serviceType",
            name: "Service Type",
            // filter: {
            //   field: "serviceType",
            //   type: "text",
            // },
            // sortable: true,
            cellRenderer: (rowItem) => (
              <Text
                weight={500}
                onClick={() => handleNavigation(rowItem)}
                style={{ cursor: "pointer" }}
              >
                {rowItem.serviceType?.name}
              </Text>
            ),
          },
          {
            field: "revenueCategory",
            name: "Revenue Group",
            // filter: {
            //   field: "revenueCategory",
            //   type: "text",
            // },
            // sortable: true,
            cellRenderer: (rowItem) => (
              <Text
                weight={500}
                onClick={() => handleNavigation(rowItem)}
                style={{ cursor: "pointer" }}
              >
                {rowItem.revenueCategory?.name}
              </Text>
            ),
          },
          ...(isLocationService
            ? [
                {
                  field: "orgLevel",
                  name: "Org-Level",
                  sortable: true,
                  cellRenderer: (rowItem: any) => (
                    <Text weight={500} style={{ cursor: "pointer" }}>
                      {rowItem.locationServices.length > 1 ? "true" : "false"}
                    </Text>
                  ),
                },
              ]
            : []),
          {
            field: "organizationservice_createdDate",
            name: "Created",
            filter: {
              field: "createdDate",
              type: "dateRange",
            },
            sortable: true,
            valueParser: (rowItem) => {
              return new Date(rowItem.createdDate).toDateString();
            },
          },
        ]}
        dataFetch={getOrganizationServices}
      />
    );
  }, [isLocationService, currentLocation]);

  if (isLocationService && !currentLocation)
    return (
      <Text align="center" fw={700} size={"xl"} mt={"xl"}>
        Current Location Not found
      </Text>
    );
  return (
    <div>
      {isAllowWrite && (
        <Button
          style={{ marginBottom: 20 }}
          onClick={isLocationService ? handleModalLocService : handleModal}
        >
          Create +
        </Button>
      )}

      <ShowTable />
    </div>
  );
};

export default ServiceList;
